import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const NavigationSidebar = ({ activeSection, onSectionClick, isMobileMenuOpen, onMobileMenuClose }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const navigationSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'Play',
      subsections: [
        { id: 'overview', title: 'Platform Overview' },
        { id: 'first-steps', title: 'Your First Steps' },
        { id: 'user-roles', title: 'User Roles' }
      ]
    },
    {
      id: 'load-ratio-science',
      title: 'Load Ratio Science',
      icon: 'TrendingUp',
      subsections: [
        { id: 'acute-chronic', title: 'Acute vs Chronic Load' },
        { id: 'calculations', title: 'Load Calculations' },
        { id: 'risk-bands', title: 'Risk Band System' },
        { id: 'interpretation', title: 'Data Interpretation' }
      ]
    },
    {
      id: 'wellness-tracking',
      title: 'Wellness Tracking',
      icon: 'Heart',
      subsections: [
        { id: 'hooper-index', title: 'Hooper Index Method' },
        { id: 'wellness-components', title: 'Wellness Components' },
        { id: 'tracking-tips', title: 'Effective Tracking' }
      ]
    },
    {
      id: 'maturation-monitoring',
      title: 'Maturation Monitoring',
      icon: 'Users',
      subsections: [
        { id: 'maturation-overview', title: 'Growth Tracking Overview' },
        { id: 'phv-explained', title: 'Peak Height Velocity' },
        { id: 'measurement-guidance', title: 'Measurement Guidelines' },
        { id: 'growth-insights', title: 'Growth Insights' }
      ]
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      icon: 'Award',
      subsections: [
        { id: 'data-consistency', title: 'Data Consistency' },
        { id: 'training-planning', title: 'Training Planning' },
        { id: 'recovery-strategies', title: 'Recovery Strategies' }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleSectionClick = (sectionId) => {
    onSectionClick(sectionId);
    if (window.innerWidth < 768) {
      onMobileMenuClose();
    }
  };

  useEffect(() => {
    // Auto-expand section containing active subsection
    navigationSections?.forEach(section => {
      const hasActiveSubsection = section?.subsections?.some(sub => sub?.id === activeSection);
      if (hasActiveSubsection) {
        setExpandedSections(prev => ({
          ...prev,
          [section?.id]: true
        }));
      }
    });
  }, [activeSection]);

  const sidebarContent = (
    <div className="h-full bg-surface border-r border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="BookOpen" size={20} color="var(--color-primary)" strokeWidth={2} />
          <span>How It Works</span>
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          Learn about load recovery methodology
        </p>
      </div>

      <nav className="p-4 space-y-2 overflow-y-auto">
        {navigationSections?.map((section) => (
          <div key={section?.id} className="space-y-1">
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-muted transition-colors duration-150"
            >
              <div className="flex items-center space-x-3">
                <Icon 
                  name={section?.icon} 
                  size={18} 
                  color="var(--color-primary)" 
                  strokeWidth={2} 
                />
                <span className="font-medium text-text-primary">{section?.title}</span>
              </div>
              <Icon 
                name="ChevronRight" 
                size={16} 
                color="currentColor"
                className={`transition-transform duration-150 ${
                  expandedSections?.[section?.id] ? 'rotate-90' : ''
                }`}
              />
            </button>

            {expandedSections?.[section?.id] && (
              <div className="ml-6 space-y-1">
                {section?.subsections?.map((subsection) => (
                  <button
                    key={subsection?.id}
                    onClick={() => handleSectionClick(subsection?.id)}
                    className={`w-full text-left p-2 rounded-md text-sm transition-colors duration-150 ${
                      activeSection === subsection?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                    }`}
                  >
                    {subsection?.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-border">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Lightbulb" size={16} color="var(--color-warning)" strokeWidth={2} />
            <span className="text-sm font-medium text-text-primary">Quick Tip</span>
          </div>
          <p className="text-xs text-text-secondary">
            Bookmark frequently used sections for quick access during training planning.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-80 h-full">
        {sidebarContent}
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={onMobileMenuClose}
          />
          <div className="fixed left-0 top-0 bottom-0 w-80 z-50 md:hidden">
            {sidebarContent}
          </div>
        </>
      )}
    </>
  );
};

export default NavigationSidebar;